// /invite/:token — redeems a one-time invite link that promotes the account to
// an LLM-enabled tier. Public route: if the visitor isn't logged in we send them
// through login/register (preserving the return path), then auto-redeem on
// return. Mirrors the backend POST /invites/{token}/redeem semantics.
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./auth-context";
import { AuthLayout } from "./auth-layout";
import { Alert, Button, Spinner } from "@/components/ui";
import { redeemInvite } from "@/api/invites";
import { ApiError } from "@/api/client";

type Phase = "checking" | "redeeming" | "success" | "error";

export function InviteRedeemPage() {
  const { t } = useTranslation();
  const { token = "" } = useParams();
  const { status, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("checking");
  const [message, setMessage] = useState<string>("");
  const attempted = useRef(false);

  useEffect(() => {
    if (status !== "authenticated" || attempted.current || !token) return;
    attempted.current = true;
    setPhase("redeeming");
    void (async () => {
      try {
        await redeemInvite(token);
        // Pull the updated tier into the user context so the UI unblocks.
        await refreshMe();
        setPhase("success");
      } catch (err) {
        setPhase("error");
        if (err instanceof ApiError && err.status === 410) {
          setMessage(t("This invite link has expired. Ask for a new one."));
        } else if (err instanceof ApiError && err.status === 409) {
          setMessage(t("This invite link has already been used."));
        } else if (err instanceof ApiError && err.status === 404) {
          setMessage(t("This invite link is not valid."));
        } else {
          setMessage(
            err instanceof Error ? err.message : t("Couldn't redeem this invite."),
          );
        }
      }
    })();
  }, [status, token, refreshMe, t]);

  const next = `/invite/${encodeURIComponent(token)}`;

  // Not logged in → route through auth, returning here afterwards.
  if (status === "unauthenticated") {
    return (
      <AuthLayout
        title={t("You're invited to EduPause")}
        subtitle={t("Log in or create an account to activate your access.")}
        footer={null}
      >
        <div className="space-y-3">
          <Link to={`/login?next=${encodeURIComponent(next)}`} className="block">
            <Button size="lg" className="w-full">
              {t("Sign in")}
            </Button>
          </Link>
          <Link to={`/register?next=${encodeURIComponent(next)}`} className="block">
            <Button size="lg" variant="secondary" className="w-full">
              {t("Create an account")}
            </Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t("Activating your access")}
      subtitle={
        phase === "success"
          ? t("You're all set.")
          : t("Hang tight while we apply your invite.")
      }
      footer={null}
    >
      {(phase === "checking" || phase === "redeeming") && (
        <div className="flex justify-center py-6 text-brand-600">
          <Spinner className="h-7 w-7" />
        </div>
      )}
      {phase === "success" && (
        <div className="space-y-4">
          <Alert tone="info">
            {t("Generation is now enabled for your account. Time to create something.")}
          </Alert>
          <Button size="lg" className="w-full" onClick={() => navigate("/app/create")}>
            {t("Start creating")}
          </Button>
        </div>
      )}
      {phase === "error" && (
        <div className="space-y-4">
          <Alert>{message}</Alert>
          <Button
            size="lg"
            variant="secondary"
            className="w-full"
            onClick={() => navigate("/app")}
          >
            {t("Go to the app")}
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}
